import { ErrorAlert } from "@components/ErrorAlert";
import { queriesPayers, queriesPayrollSchedules } from "@query";
import {
  WSActions,
  WSAlert,
  WSButton,
  WSControl,
  WSElement,
  WSForm,
  WSList,
  WSLoader,
  WSPanel,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayrollScheduleType } from "@wingspanhq/wingspan-api-sdk";
import React from "react";
import * as Yup from "yup";
import {
  FieldsPayrollCadence,
  payrollCadenceValidationSchema
} from "../../../../shared/components/FieldsPayrollCadence";
import { selectorPayrollCadence } from "../../../../shared/selectors/selectorPayrollCadence";
import { selectorPayrollCadenceFormData } from "../../../../shared/selectors/selectorPayrollCadenceFormData";
import { openInNewTab } from "../../../../shared/utils/openInNewTab";
import { FormData } from "./types";
import { useMutationSubmit } from "./useMutationSubmit";

/**
 * The payroll-schedule step of payer onboarding, on the same two v3 resources as
 * the payroll settings screen: `runsPayroll` on `PayerSettings`, the cadence on
 * the payer's `ContractorVendor` `PayrollSchedule`.
 *
 * The step no longer offers the multi-stage approval toggle, the 1099 issuing
 * flag, or the pay-approved-only-when-due day count that the v1 form wrote: v3
 * projects no approval strategy or tax-form policy at all, and rejects a write
 * of the Wingspan-managed day-count fields. The settings screen dropped the same
 * three — see docs/known-gaps.md § payer-settings.
 */
type Props = { onSuccess?: () => void };

export const FormPayrollSettings: React.FC<Props> = ({ onSuccess }) => {
  const queryPayerSettings = queriesPayers.getCurrentPayerSettings.useQuery();
  const querySchedules = queriesPayrollSchedules.listPayrollSchedules.useQuery(
    { filter: { type: { eq: PayrollScheduleType.CONTRACTOR_VENDOR } } },
    // A payer onboarding for the first time may have no cadence to read yet, and
    // retrying a read the form can proceed without only delays the step.
    { retry: false }
  );

  const [submit, submitMeta] = useMutationSubmit({ onSuccess });

  const payrollSettings = queryPayerSettings.data?.payrollSettings;
  const schedule = querySchedules.data?.data?.[0];

  // A schedule can come back with its pay dates but without the frequency and
  // interval that produced them; inverting the dates recovers the same cadence.
  const derivedCadence = selectorPayrollCadence(schedule?.scheduleDates);

  // `WSForm` captures its defaults once, so the form waits for both reads
  // instead of mounting on the fallback cadence and saving that back.
  if (queryPayerSettings.isLoading || querySchedules.isLoading) {
    return <WSLoader.Spinner />;
  }

  if (queryPayerSettings.error) {
    return (
      <ErrorAlert context="PayrollSettings" error={queryPayerSettings.error} />
    );
  }

  return (
    <WSForm<FormData>
      defaultValues={{
        runsPayroll: payrollSettings?.runsPayroll ?? true,
        ...selectorPayrollCadenceFormData({ schedule, derivedCadence })
      }}
      validationSchema={Yup.object().shape(payrollCadenceValidationSchema)}
      onSubmit={submit}
    >
      <WSList gap="2XL">
        <WSPanel>
          <WSElement mb="2XL">
            <WSSectionToolbar title="Payroll schedule" />
            <WSText.ParagraphSm color="gray500">
              Enable payroll to set how often your contractors receive payments.
              If you prefer to not set a schedule, disable payroll.
            </WSText.ParagraphSm>
          </WSElement>

          <WSList gap="2XL">
            {/* Only warn once both sources are gone. With the cadence read down
                but pay dates present, the derivation gives the payer their real
                setting; with neither, the radios would present the fallback as
                though it were saved, which is a confident lie. */}
            {!!querySchedules.error && !derivedCadence && (
              <WSAlert theme="warning" icon="alert">
                Your payroll cadence can't be loaded right now, so the selection
                below isn't your saved setting. Saving will still apply the
                cadence you choose.
              </WSAlert>
            )}

            <WSForm.Field
              name="runsPayroll"
              component={WSControl}
              componentProps={{
                type: "switch",
                label: "Enable Payroll"
              }}
            />
            {/* Hidden rather than unmounted: react-hook-form v6 drops an
                unmounted field from the submitted values, which would fail the
                cadence schema — and the message would render inside the fields
                the payer can't see, so the step would refuse to advance with
                nothing on screen to explain why. */}
            <WSForm.Value name="runsPayroll">
              {runsPayroll => (
                <WSElement hidden={!runsPayroll}>
                  <FieldsPayrollCadence label="Expected pay date" />
                </WSElement>
              )}
            </WSForm.Value>

            <WSText.ParagraphSm weight="light">
              Learn more about{" "}
              <WSButton.Link
                size="M"
                type="button"
                onClick={() => {
                  openInNewTab(
                    // TODO(onb): Update link to point to the correct location
                    "https://docs.wingspan.app/docs/payroll-settings"
                  );
                }}
              >
                payroll schedule
              </WSButton.Link>
            </WSText.ParagraphSm>
          </WSList>
        </WSPanel>

        <ErrorAlert context="PayrollSettings" error={submitMeta.error} />

        <WSActions
          orientation="vertical"
          buttons={[
            { label: "Save & continue", loading: submitMeta.isLoading }
          ]}
        />
        <WSText.ParagraphXs weight="book" color="gray400">
          Payroll settings (payroll schedule and payables settings) can be
          viewed and updated at any time in your settings.
        </WSText.ParagraphXs>
      </WSList>
    </WSForm>
  );
};
